// Paths whose value should sort as "0" when null, instead of the default
// comparison (recurring/work tabs' date and name columns, Key Contacts'
// nullable metrics) — these are different columns from different tabs that
// all need the same null-as-"0" treatment.
const ZERO_WHEN_NULL_PATHS = [
  "billThruDate",
  "endDate",
  "dueDate",
  "closedDate",
  "costCenterName",
  "ownerName",
  "projectManager",
  "costCenterNumber",
  "confidenceLevel",
  "advocacyLevel",
  "discProfile",
  "relationshipSteward",
  "budget",
  "spentBillable",
  "spentNonBillable",
];

const resolve = (obj: any, path: string) => {
  let properties = Array.isArray(path) ? path : path.split(".");
  // When order is by a boolean, we should cast to string
  // because on 'false' value never reach the sort on next line (aprox. 23)
  if (path === "achieved") {
    return (properties as any).reduce(
      (prev: any, curr: string) => prev?.[curr]?.toString(),
      obj
    );
  }
  if (ZERO_WHEN_NULL_PATHS.includes(path) && obj[path] === null) {
    return (properties as any).reduce((prev: any) => prev && "0", obj);
  }

  // On resources tab and column description, we use two diff properties to show info,
  // description and purpose (Resources.tsx), so we should use "purpose" when obj['description'] is null
  if (path === "description" && obj[path] === null) {
    return (properties as any).reduce(
      (prev: any) => prev?.["purpose"]?.toString(),
      obj
    );
  }

  // Spotlight report summary - successCriteriaAmount
  if (path === "successCriteriaAmount") {
    return (properties as any).reduce(
      (prev: any, curr: string) => prev && getNumberFromString(prev[curr]),
      obj
    );
  }

  // users - all properties inside userProfile object
  if (
    path === "jobTitle" ||
    path === "employmentStatus" ||
    path === "supervisorName" ||
    path === "priceLevel"
  ) {
    return (properties as any).reduce((prev: any, curr: string) => {
      return prev.userProfile?.[curr] ?? "1";
    }, obj);
  }
  // users - all properties inside costCenter object
  if (path === "costCenter") {
    return (properties as any).reduce((prev: any, curr: string) => {
      // sort cc in timeconfirmation is string, in users is by number
      return typeof prev.costCenter === "string"
        ? prev?.[curr]
        : prev.costCenter?.number ?? "1";
    }, obj);
  }

  if (path === "usersAssignees") {
    return (properties as any).reduce((prev: any, curr: string) => {
      return prev?.[curr] ? prev[curr][0].name : "";
    }, obj);
  }

  if (path === "fixedPrice") {
    return (properties as any).reduce(
      (prev: any) =>
        prev?.["projectBillingId"] === 2
          ? prev["estimatedPrice"]
          : prev["fixedPrice"],
      obj
    );
  }

  return (properties as any).reduce(
    (prev: any, curr: string) => prev?.[curr],
    obj
  );
};

// helper functions to order successCriteriaAmount by none, achieve and then numbers
const getNumberFromString = (prev) => {
  if (prev === "None") {
    return -2;
  } else if (prev === "Achieved") {
    return -1;
  } else {
    return Number(prev);
  }
};

export const toggleSortCriteria = (
  currentCriteria: "asc" | "desc"
): "asc" | "desc" => (currentCriteria === "asc" ? "desc" : "asc");

export const sortDataBy = <T>(
  data: Array<T>,
  sortBy: string,
  criteria: "asc" | "desc",
  type: "date" | "string" | "number" | undefined
): T[] => {
  if (!data || resolve(data[0], sortBy) === null) return data;

  const result = data.slice().sort((a: T, b: T) => {
    let first, last;
    if (criteria === "asc") {
      first = a;
      last = b;
    } else {
      first = b;
      last = a;
    }
    switch (type) {
      case "date":
        return (
          new Date(resolve(first, sortBy) as unknown as string).getTime() -
          new Date(resolve(last, sortBy) as unknown as string).getTime()
        );
      case "string":
        return (resolve(first, sortBy) as unknown as string).localeCompare(
          resolve(last, sortBy) as unknown as string
        );
      case "number":
        return (
          (resolve(first, sortBy) as unknown as number) -
          (resolve(last, sortBy) as unknown as number)
        );
      default:
        return (resolve(first, sortBy) as any) - (resolve(last, sortBy) as any);
    }
  });

  return result;
};
